import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import { AnchorResolver } from '@src/editor/models/layout/AnchorResolver';
import { LayoutSegmentFactory } from '@src/editor/models/layout/LayoutSegmentFactory';
import { LayoutTemplateRegistry } from '@src/editor/models/layout/LayoutTemplateRegistry';
import { mapFitTypeToTemplateId } from '@src/editor/models/layout/legacyLayoutMapping';
import { Scene } from '@src/editor/models/layout/Scene';
import { TranscriptTrack } from '@src/editor/models/TranscriptTrack';

import { LogManager } from '../utility/Logger';

import type { Element } from '@src/editor';
import type { PasteInsertionResult } from '@src/editor/commands/transcript/TranscriptPasteCommand';
import type { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';
import type {
  AnchorType,
  ElementAnchor,
  IScene,
} from '@src/editor/models/layout/types';
import type { Timeline } from '@src/editor/models/Timeline';

export enum ValidationStatus {
  VALID = 'VALID',
  INVALID = 'INVALID',
  NO_OP = 'NO_OP',
}

/**
 * Validation result for scene operations
 */
export interface ValidationResult {
  status: ValidationStatus;
  error?: string;
}

export interface SceneAnchorSnapshot {
  anchorStart: AnchorType;
  anchorEnd: AnchorType;
}

export interface SceneAnchorUpdateResult {
  updatedScene: Scene;
  previousAnchors: SceneAnchorSnapshot;
}

/**
 * Captured scene information with resolved anchor times
 */
export interface CapturedSceneInfo {
  sceneId: string;
  scene: Scene;
  sceneStartTime: number;
  sceneEndTime: number;
}

/**
 * Result of scene adjustment after trim operation
 */
export interface TrimAdjustmentResult {
  /** Scenes that were modified (anchors updated) */
  modifiedScenes: Scene[];
  /** Scenes that were removed (both anchors in trimmed region) */
  removedScenes: Scene[];
  /** Scenes that were unchanged */
  unchangedScenes: Scene[];
}

/**
 * Information about scenes adjusted when adding a new scene with overlap prevention
 */
export interface AdjustedSceneInfo {
  sceneId: string;
  originalSnapshot: Scene;
}

/**
 * Result of overlap adjustment when adding a scene
 */
export interface OverlapAdjustmentResult {
  /** Scenes that were modified (with original snapshots for undo) */
  modifiedScenes: AdjustedSceneInfo[];
  /** IDs of new scenes created from splits (for undo removal) */
  addedSceneIds: string[];
  /** IDs of scenes that were removed because new scene completely covered them */
  removedSceneIds: string[];
}

/**
 * Information about trim from start.
 * Captured BEFORE the element is modified.
 */
export interface TrimStartInfo {
  elementId: string;
  /** Original media start time before trim */
  originalMediaStart: number;
  /** Original media end time before trim */
  originalMediaEnd: number;
  /** New media start time after trim (amount trimmed) */
  newMediaStart: number;
}

/**
 * Information about trim from end.
 * Captured BEFORE the element is modified.
 */
export interface TrimEndInfo {
  elementId: string;
  /** Original media start time before trim */
  originalMediaStart: number;
  /** Original media end time before trim */
  originalMediaEnd: number;
  /** New media end time after trim */
  newMediaEnd: number;
}

/**
 * Information about element deletion for scene adjustment.
 * Captured BEFORE the element is deleted.
 */
export interface DeleteInfo {
  /** ID of the element being deleted */
  deletedElementId: string;
  /** ID of the previous element in the track (if any) */
  previousElementId: string | null;
  /** Media end time of the previous element (if any) */
  previousElementMediaEnd: number | null;
  /** ID of the next element in the track (if any) */
  nextElementId: string | null;
  /** Media start time of the next element (if any) */
  nextElementMediaStart: number | null;
}

/**
 * Information about excise operation (cutting from middle of element).
 * Captured BEFORE the element is split into two parts.
 */
export interface ExciseInfo {
  /** ID of the original element being excised */
  originalElementId: string;
  /** Original media start time of the element before excise */
  originalMediaStart: number;
  /** Original media end time of the element before excise */
  originalMediaEnd: number;
  /** Media start time of the excised section */
  excisionMediaStart: number;
  /** Media end time of the excised section */
  excisionMediaEnd: number;
  /** ID of the new left element (before excision) */
  leftElementId: string;
  /** ID of the new right element (after excision) */
  rightElementId: string;
  /** Media start time of the left element (same as original) */
  leftMediaStart: number;
  /** Media end time of the left element */
  leftMediaEnd: number;
  /** Media start time of the right element */
  rightMediaStart: number;
  /** Media end time of the right element (same as original) */
  rightMediaEnd: number;
}

/**
 * Information about split operation (splitting element into two parts at a point).
 * Captured BEFORE the element is split.
 */
export interface SplitInfo {
  /** ID of the original element being split */
  originalElementId: string;
  /** Original media start time of the element before split */
  originalMediaStart: number;
  /** Original media end time of the element before split */
  originalMediaEnd: number;
  /** Media time where the split occurs */
  splitMediaTime: number;
  /** ID of the new left element (before split point) */
  leftElementId: string;
  /** ID of the new right element (after split point) */
  rightElementId: string;
}

/**
 * Information about extend start (un-trim from start).
 * Captured BEFORE the element is extended.
 */
export interface ExtendStartInfo {
  elementId: string;
  /** Original media start time before extend */
  originalMediaStart: number;
  /** Original media end time before extend */
  originalMediaEnd: number;
  /** New media start time after extend (earlier than original) */
  newMediaStart: number;
}

/**
 * Information about extend end (un-trim from end).
 * Captured BEFORE the element is extended.
 * Note: Extending end requires no scene adjustment as anchor positions remain valid.
 */
export interface ExtendEndInfo {
  elementId: string;
  /** Original media start time before extend */
  originalMediaStart: number;
  /** Original media end time before extend */
  originalMediaEnd: number;
  /** New media end time after extend (later than original) */
  newMediaEnd: number;
}

/**
 * Information about merge operation (merging two elements into one).
 * Captured BEFORE the elements are merged.
 */
export interface MergeInfo {
  /** ID of the first element being merged */
  firstElementId: string;
  /** Media start time of the first element */
  firstMediaStart: number;
  /** Media end time of the first element */
  firstMediaEnd: number;
  /** ID of the second element being merged */
  secondElementId: string;
  /** Media start time of the second element */
  secondMediaStart: number;
  /** Media end time of the second element */
  secondMediaEnd: number;
  /** ID of the new merged element */
  mergedElementId: string;
  /** Media start time of the merged element (same as first) */
  mergedMediaStart: number;
  /** Media end time of the merged element (same as second) */
  mergedMediaEnd: number;
}

const logger = LogManager.getLogger('SceneService');

/**
 * SceneService
 * Handles business logic for scene management
 *
 * Responsibilities:
 * - Validation of scenes (track existence, template validity, anchor validity)
 * - Scene mutations (add, remove, update)
 * - Queries (find overlapping scenes, get by criteria)
 *
 * Does NOT store state - operates on Timeline data
 */
export class SceneService {
  private anchorResolver = new AnchorResolver();

  cloneScene(scene: Scene): Scene {
    return new Scene({
      anchorStart: this.cloneAnchor(scene.anchorStart),
      anchorEnd: this.cloneAnchor(scene.anchorEnd),
      layout: scene.layout.clone(),
    });
  }

  /**
   * Validates a scene before adding to timeline
   */
  validateScene(timeline: Timeline, scene: Scene): ValidationResult {
    // Check if template exists
    const registry = LayoutTemplateRegistry.getInstance();
    const template = registry.getTemplate(scene.layout.layoutTemplateId);

    if (!template) {
      return {
        status: ValidationStatus.INVALID,
        error: `Layout template '${scene.layout.layoutTemplateId}' not found`,
      };
    }

    // Check if all bound tracks exist
    // Exception: karaoke bindings are allowed even if track 32 doesn't exist yet
    // (LayoutEngine will handle missing tracks gracefully)
    for (const [slotName, binding] of Object.entries(scene.layout.bindings)) {
      // Skip validation for karaoke slot - it may not exist yet
      if (
        slotName === 'karaoke' &&
        binding.trackId === String(KARAOKE_TRACK_NUMBER)
      ) {
        continue;
      }

      const track = timeline.getTrack(binding.trackId);
      if (!track) {
        return {
          status: ValidationStatus.INVALID,
          error: `Track '${binding.trackId}' referenced in slot '${slotName}' does not exist`,
        };
      }
    }

    // Validate anchors - check if element IDs exist for element anchors
    const validateAnchor = (
      anchor: typeof scene.anchorStart,
    ): ValidationResult => {
      if (typeof anchor === 'object' && 'elementId' in anchor) {
        const element = timeline.getElement(anchor.elementId);
        if (!element) {
          return {
            status: ValidationStatus.INVALID,
            error: `Element '${anchor.elementId}' referenced in anchor does not exist`,
          };
        }
      }
      return { status: ValidationStatus.VALID };
    };

    const startAnchorValidation = validateAnchor(scene.anchorStart);
    if (startAnchorValidation.status === ValidationStatus.INVALID)
      return startAnchorValidation;

    const endAnchorValidation = validateAnchor(scene.anchorEnd);
    if (endAnchorValidation.status === ValidationStatus.INVALID)
      return endAnchorValidation;

    return { status: ValidationStatus.VALID };
  }

  /**
   * Adds a scene to the timeline
   * @param timeline The timeline to add the scene to
   * @param scene The scene to add
   * @param overlapStrategy When 'carve-out', existing scenes that overlap with the
   *        new scene will have their anchors adjusted (split/trimmed) to make room.
   *        Default 'none' adds without adjustments.
   * @returns Overlap adjustment result (for undo support). Empty arrays if no adjustments made.
   * @throws Error if validation fails
   */
  addScene(
    timeline: Timeline,
    scene: Scene,
    overlapStrategy: 'none' | 'carve-out' = 'none',
  ): OverlapAdjustmentResult {
    const validation = this.validateScene(timeline, scene);

    if (validation.status === ValidationStatus.INVALID) {
      throw new Error(`Cannot add scene: ${validation.error}`);
    }

    // Optionally adjust existing scenes to prevent overlap
    const adjustmentResult: OverlapAdjustmentResult =
      overlapStrategy === 'carve-out'
        ? this.carveSceneIntoExisting(timeline, scene)
        : { modifiedScenes: [], addedSceneIds: [], removedSceneIds: [] };

    const currentScenes = [...timeline.scenes];
    currentScenes.push(scene);
    timeline.setScenes(this.sortScenes(currentScenes, timeline));

    return adjustmentResult;
  }

  /**
   * Carves out space for a new scene by adjusting overlapping existing scenes.
   * - Scenes that completely wrap the new scene are split into two parts
   * - Scenes that start before and extend into the new scene have their end trimmed
   * - Scenes that start within the new scene's range have their start adjusted
   * @returns Overlap adjustment result with modified scenes and added scene IDs
   */
  private carveSceneIntoExisting(
    timeline: Timeline,
    newScene: Scene,
  ): OverlapAdjustmentResult {
    const modifiedScenes: AdjustedSceneInfo[] = [];
    const addedSceneIds: string[] = [];
    const scenesToAdd: Scene[] = [];

    let newSceneStart: number;
    let newSceneEnd: number;

    try {
      newSceneStart = this.anchorResolver.resolve(
        newScene.anchorStart,
        timeline,
      );
      newSceneEnd = this.anchorResolver.resolve(newScene.anchorEnd, timeline);
    } catch {
      // Can't resolve new scene anchors, skip adjustment
      return { modifiedScenes, addedSceneIds, removedSceneIds: [] };
    }

    const scenesToRemove: string[] = [];

    for (const existingScene of timeline.scenes) {
      let existingStart: number;
      let existingEnd: number;

      try {
        existingStart = this.anchorResolver.resolve(
          existingScene.anchorStart,
          timeline,
        );
        existingEnd = this.anchorResolver.resolve(
          existingScene.anchorEnd,
          timeline,
        );
      } catch {
        // Can't resolve existing scene, skip
        continue;
      }

      // Check for overlap
      if (existingEnd <= newSceneStart || existingStart >= newSceneEnd) {
        // No overlap
        continue;
      }

      // Snapshot before modification
      const originalSnapshot = Scene.fromJSON(existingScene.toJSON());

      // Case 0: New scene completely covers (or equals) the existing scene
      // -> Remove the existing scene entirely (the new scene replaces it)
      if (existingStart >= newSceneStart && existingEnd <= newSceneEnd) {
        modifiedScenes.push({ sceneId: existingScene.id, originalSnapshot });
        scenesToRemove.push(existingScene.id);
        continue;
      }

      // Case 1: Existing scene completely wraps the new scene
      // -> Split: trim end to new start, create continuation from new end to old end
      if (existingStart < newSceneStart && existingEnd > newSceneEnd) {
        const newAnchorEnd = this.createAnchorForTimelineTime(
          timeline,
          newScene.anchorStart,
          newSceneStart,
        );
        if (newAnchorEnd) {
          existingScene.setAnchorEnd(newAnchorEnd);
          modifiedScenes.push({ sceneId: existingScene.id, originalSnapshot });

          // Create continuation scene for the portion after the new scene
          const continuationAnchorStart = this.createAnchorForTimelineTime(
            timeline,
            newScene.anchorEnd,
            newSceneEnd,
          );
          if (continuationAnchorStart) {
            const continuationScene = new Scene({
              anchorStart: continuationAnchorStart,
              anchorEnd: originalSnapshot.anchorEnd,
              layout: existingScene.layout.clone(),
            });
            scenesToAdd.push(continuationScene);
          }
        }
      }
      // Case 2: Existing scene starts before new scene and extends into it (but doesn't wrap)
      // -> Trim existing scene's end to new scene's start
      else if (existingStart < newSceneStart && existingEnd > newSceneStart) {
        const newAnchorEnd = this.createAnchorForTimelineTime(
          timeline,
          newScene.anchorStart,
          newSceneStart,
        );
        if (newAnchorEnd) {
          existingScene.setAnchorEnd(newAnchorEnd);
          modifiedScenes.push({ sceneId: existingScene.id, originalSnapshot });
        }
      }
      // Case 3: Existing scene starts within new scene's range
      // -> Move existing scene's start to new scene's end
      else if (existingStart >= newSceneStart && existingStart < newSceneEnd) {
        const newAnchorStart = this.createAnchorForTimelineTime(
          timeline,
          newScene.anchorEnd,
          newSceneEnd,
        );
        if (newAnchorStart) {
          existingScene.setAnchorStart(newAnchorStart);
          modifiedScenes.push({ sceneId: existingScene.id, originalSnapshot });
        }
      }
    }

    // Remove scenes that are completely covered by the new scene
    if (scenesToRemove.length > 0) {
      const remainingScenes = timeline.scenes.filter(
        s => !scenesToRemove.includes(s.id),
      );
      timeline.setScenes(remainingScenes);
    }

    // Add any continuation scenes created from splits
    for (const scene of scenesToAdd) {
      const currentScenes = [...timeline.scenes];
      currentScenes.push(scene);
      timeline.setScenes(this.sortScenes(currentScenes, timeline));
      addedSceneIds.push(scene.id);
    }

    return { modifiedScenes, addedSceneIds, removedSceneIds: scenesToRemove };
  }

  /**
   * Creates an anchor for a specific timeline time, using a reference anchor
   * to determine the element and calculate relative time.
   */
  private createAnchorForTimelineTime(
    timeline: Timeline,
    referenceAnchor: AnchorType,
    timelineTime: number,
  ): AnchorType | null {
    if (
      typeof referenceAnchor !== 'object' ||
      !('elementId' in referenceAnchor)
    ) {
      return null;
    }

    const element = timeline.getElement(referenceAnchor.elementId);
    if (!element) {
      return null;
    }

    // Calculate relativeTime: timeline time relative to element's start
    const relativeTime = timelineTime - element.time;

    // Normalize: if relativeTime matches element duration, use end: true form
    const elementDuration = element.durationSeconds;
    const isAtEnd =
      elementDuration > 0 && Math.abs(relativeTime - elementDuration) < 0.0001;

    if (isAtEnd) {
      return {
        elementId: referenceAnchor.elementId,
        relativeTime: null,
        end: true,
      };
    }

    return {
      elementId: referenceAnchor.elementId,
      relativeTime: relativeTime > 0 ? relativeTime : null,
      end: false,
    };
  }

  /**
   * Removes a scene from the timeline
   * @returns true if scene was removed, false if not found
   */
  removeScene(timeline: Timeline, sceneId: string): boolean {
    const currentScenes = timeline.scenes;
    const index = currentScenes.findIndex(scene => scene.id === sceneId);

    if (index === -1) {
      return false;
    }

    const newScenes = [
      ...currentScenes.slice(0, index),
      ...currentScenes.slice(index + 1),
    ];
    timeline.setScenes(newScenes);
    return true;
  }

  /**
   * Replaces all scenes on the timeline with a new set of scenes
   * Validates all scenes before replacing
   * @throws Error if any scene validation fails
   */
  replaceScenes(timeline: Timeline, newScenes: Scene[]): void {
    // Validate all scenes before replacing
    for (const scene of newScenes) {
      const validation = this.validateScene(timeline, scene);
      if (validation.status === ValidationStatus.INVALID) {
        throw new Error(`Cannot replace scenes: ${validation.error}`);
      }
    }

    timeline.setScenes(newScenes);
  }

  /**
   * Updates a scene
   * @returns true if scene was updated, false if not found
   * @throws Error if validation fails
   */
  updateScene(
    timeline: Timeline,
    sceneId: string,
    updatedScene: Scene,
  ): boolean {
    const currentScenes = timeline.scenes;
    const index = currentScenes.findIndex(scene => scene.id === sceneId);

    if (index === -1) {
      return false;
    }

    // Validate the updated scene
    const validation = this.validateScene(timeline, updatedScene);
    if (validation.status === ValidationStatus.INVALID) {
      throw new Error(`Cannot update scene: ${validation.error}`);
    }

    const newScenes = [...currentScenes];
    newScenes[index] = updatedScene;
    timeline.setScenes(this.sortScenes(newScenes, timeline));
    return true;
  }

  /**
   * Gets a scene by ID
   */
  getScene(timeline: Timeline, sceneId: string): Scene | undefined {
    return timeline.scenes.find(scene => scene.id === sceneId);
  }

  /**
   * Gets all scenes
   */
  getAllScenes(timeline: Timeline): readonly Scene[] {
    return timeline.scenes;
  }

  /**
   * Finds scenes that use a specific track in their bindings.
   */
  findScenesByTrack(timeline: Timeline, trackId: string): Scene[] {
    return timeline.scenes.filter(scene => {
      return Object.values(scene.layout.bindings).some(
        binding => binding.trackId === trackId,
      );
    });
  }

  /**
   * Finds the most relevant scene for a given element.
   * Priority:
   * 1. Scene whose resolved time range contains the reference time (preferring anchors targeting the element)
   * 2. Scene whose resolved time range overlaps the element's playback window (preferring anchors)
   * 3. Scene directly anchored to this element
   * 4. Scene with 'clip-start'/'clip-end' anchors (general clip boundaries)
   * 5. Earliest scene on the track
   *
   * @param timeline The timeline to search in
   * @param elementId The element ID to evaluate
   * @param referenceTime Optional absolute time to bias selection
   */
  findSceneForElement(
    timeline: Timeline,
    elementId: string,
    referenceTime?: number,
  ): Scene | undefined {
    const element = timeline.getElement(elementId);
    if (!element || element.track === null) {
      return undefined;
    }

    const scenesForTrack = this.findScenesByTrack(
      timeline,
      String(element.track),
    );

    if (scenesForTrack.length === 0) {
      return undefined;
    }

    const resolvedScenes = scenesForTrack
      .map(scene => {
        try {
          const resolvedStart = this.anchorResolver.resolve(
            scene.anchorStart,
            timeline,
          );
          const resolvedEnd = this.anchorResolver.resolve(
            scene.anchorEnd,
            timeline,
          );
          const start = Math.min(resolvedStart, resolvedEnd);
          const end = Math.max(resolvedStart, resolvedEnd);
          const anchorStartMatches =
            typeof scene.anchorStart === 'object' &&
            'elementId' in scene.anchorStart &&
            scene.anchorStart.elementId === elementId;
          const anchorEndMatches =
            typeof scene.anchorEnd === 'object' &&
            'elementId' in scene.anchorEnd &&
            scene.anchorEnd.elementId === elementId;

          return {
            scene,
            start,
            end,
            anchorStartMatches,
            anchorEndMatches,
          };
        } catch (error) {
          logger.warn(
            'SceneService.findSceneForElement failed resolving anchors',
            {
              sceneId: scene.id,
              elementId,
              error,
            },
          );
          return null;
        }
      })
      .filter(
        (
          entry,
        ): entry is {
          scene: Scene;
          start: number;
          end: number;
          anchorStartMatches: boolean;
          anchorEndMatches: boolean;
        } => entry !== null,
      )
      .sort((a, b) => a.start - b.start);

    if (resolvedScenes.length === 0) {
      return scenesForTrack[0];
    }

    const elementStart = element.time ?? 0;
    const elementEnd = element.end_time ?? elementStart;
    const evaluationTime =
      typeof referenceTime === 'number' && Number.isFinite(referenceTime)
        ? referenceTime
        : elementStart;

    const preferAnchored = (
      entries: Array<{
        scene: Scene;
        start: number;
        end: number;
        anchorStartMatches: boolean;
        anchorEndMatches: boolean;
      }>,
    ): Scene | undefined => {
      const anchored = entries.find(
        entry => entry.anchorStartMatches || entry.anchorEndMatches,
      );
      return anchored?.scene ?? entries[0]?.scene;
    };

    const containingReference = resolvedScenes.filter(
      ({ start, end }) => evaluationTime >= start && evaluationTime < end,
    );

    if (containingReference.length > 0) {
      const match = preferAnchored(containingReference);
      if (match) {
        return match;
      }
    }

    const elementWindowMatches = resolvedScenes.filter(
      ({ start, end }) => elementEnd > start && elementStart < end,
    );

    if (elementWindowMatches.length > 0) {
      const match = preferAnchored(elementWindowMatches);
      if (match) {
        return match;
      }
    }

    const anchoredScene = resolvedScenes.find(
      entry => entry.anchorStartMatches || entry.anchorEndMatches,
    );
    if (anchoredScene) {
      return anchoredScene.scene;
    }

    const clipBoundaryScene = scenesForTrack.find(
      scene =>
        scene.anchorStart === 'clip-start' || scene.anchorEnd === 'clip-end',
    );
    if (clipBoundaryScene) {
      return clipBoundaryScene;
    }

    return resolvedScenes[0]?.scene ?? scenesForTrack[0];
  }

  /**
   * Finds ALL scenes bound to a specific element
   * Returns all scenes where the element is referenced in either anchor (start or end)
   * @param timeline The timeline to search in
   * @param elementId The element ID to find all scenes for
   * @returns Array of all scenes anchored to this element
   */
  findAllScenesForElement(timeline: Timeline, elementId: string): Scene[] {
    return timeline.scenes.filter(scene => {
      // Check if element is referenced in start anchor
      const anchorStart =
        typeof scene.anchorStart === 'object' &&
        'elementId' in scene.anchorStart
          ? scene.anchorStart.elementId
          : null;

      // Check if element is referenced in end anchor
      const anchorEnd =
        typeof scene.anchorEnd === 'object' && 'elementId' in scene.anchorEnd
          ? scene.anchorEnd.elementId
          : null;

      return anchorStart === elementId || anchorEnd === elementId;
    });
  }

  /**
   * Gets the layout template ID for an element
   * @param timeline The timeline to check
   * @param elementId The element ID to check
   * @returns The template ID (e.g., 'split_horizontal_16_9') or null
   */
  getLayoutTypeForElement(
    timeline: Timeline,
    elementId: string,
    referenceTime?: number,
  ): string | null {
    const scene = this.findSceneForElement(timeline, elementId, referenceTime);
    return scene ? scene.layout.layoutTemplateId : null;
  }

  /**
   * Captures scene information with resolved anchor times.
   * Useful for operations that need to pre-capture scene data before element modifications.
   *
   * @param scene The scene to capture
   * @param timeline The timeline for anchor resolution
   * @returns CapturedSceneInfo with resolved start/end times
   */
  captureSceneInfoWithResolvedTimes(
    scene: Scene,
    timeline: Timeline,
  ): CapturedSceneInfo {
    return {
      sceneId: scene.id,
      scene,
      sceneStartTime: this.anchorResolver.resolve(scene.anchorStart, timeline),
      sceneEndTime: this.anchorResolver.resolve(scene.anchorEnd, timeline),
    };
  }

  restoreSceneAnchors(
    timeline: Timeline,
    scene: Scene,
    snapshot: SceneAnchorSnapshot,
  ): void {
    scene.setAnchorStart(this.cloneAnchor(snapshot.anchorStart));
    scene.setAnchorEnd(this.cloneAnchor(snapshot.anchorEnd));

    const scenes = Array.from(timeline.scenes);
    const index = scenes.findIndex(s => s.id === scene.id);
    if (index !== -1) {
      scenes[index] = scene;
      timeline.setScenes(scenes);
    }
  }

  private cloneAnchor(anchor: AnchorType): AnchorType {
    return typeof anchor === 'string' ? anchor : { ...anchor };
  }

  public retargetAnchor(anchor: AnchorType, elementId: string): AnchorType {
    if (typeof anchor === 'string') {
      return anchor;
    }

    return {
      ...anchor,
      elementId,
    };
  }

  // ===================================================================
  // Scene Adjustment Methods - Clean separation of concerns
  // ===================================================================

  /**
   * Adjusts scenes after trimming from the start of an element.
   *
   * Algorithm:
   * 1. For each scene, check if anchors reference the trimmed element
   * 2. Calculate media time that each anchor points to
   * 3. If anchor points to trimmed region (< newMediaStart), adjust it
   * 4. If both anchors in trimmed region, remove scene
   *
   * @param timeline The timeline (element already modified)
   * @param trimInfo Pre-captured information about the trim
   * @returns Result containing modified, removed, and unchanged scenes
   */
  adjustScenesAfterTrimStart(
    timeline: Timeline,
    trimInfo: TrimStartInfo,
  ): TrimAdjustmentResult {
    const { elementId, originalMediaStart, originalMediaEnd, newMediaStart } =
      trimInfo;
    const trimmedRegionStart = originalMediaStart;
    const trimmedRegionEnd = newMediaStart;

    const modifiedScenes: Scene[] = [];
    const removedScenes: Scene[] = [];
    const unchangedScenes: Scene[] = [];

    for (const scene of timeline.scenes) {
      const { anchorStart, anchorEnd } = scene;

      // Get media times for anchors (using original element state)
      const startMediaTime = this.getAnchorMediaTime(
        anchorStart,
        elementId,
        originalMediaStart,
        originalMediaEnd,
      );
      const endMediaTime = this.getAnchorMediaTime(
        anchorEnd,
        elementId,
        originalMediaStart,
        originalMediaEnd,
      );

      // If neither anchor refers to this element, skip
      if (startMediaTime === null && endMediaTime === null) {
        unchangedScenes.push(scene);
        continue;
      }

      // Check if anchors are in trimmed region
      const startInTrimmed =
        startMediaTime !== null &&
        startMediaTime >= trimmedRegionStart &&
        startMediaTime < trimmedRegionEnd;

      const endInTrimmed =
        endMediaTime !== null &&
        endMediaTime >= trimmedRegionStart &&
        endMediaTime < trimmedRegionEnd;

      // If both anchors in trimmed region, remove scene
      if (startInTrimmed && endInTrimmed) {
        removedScenes.push(scene);
        continue;
      }

      // Adjust anchors
      let newAnchorStart = anchorStart;
      let newAnchorEnd = anchorEnd;
      let wasModified = false;

      // Handle start anchor
      if (startMediaTime !== null) {
        if (startInTrimmed) {
          // Anchor is in trimmed region - move to new element start
          newAnchorStart = {
            elementId,
            relativeTime: null,
            end: false,
          };
          wasModified = true;
        } else {
          // Anchor is not in trimmed region but refers to element
          // Adjust relativeTime to preserve position relative to media time
          const mediaTimeDelta = newMediaStart - originalMediaStart;
          const currentRelativeTime =
            typeof anchorStart === 'object' &&
            'elementId' in anchorStart &&
            anchorStart.relativeTime !== null
              ? anchorStart.relativeTime
              : startMediaTime - originalMediaStart;
          const newRelativeTime = currentRelativeTime - mediaTimeDelta;

          newAnchorStart = {
            elementId,
            relativeTime: newRelativeTime,
            end:
              typeof anchorStart === 'object' &&
              'elementId' in anchorStart &&
              anchorStart.end,
          };
          wasModified = true;
        }
      }

      // Handle end anchor
      if (endMediaTime !== null) {
        if (endInTrimmed) {
          // Anchor is in trimmed region - move to new element start
          newAnchorEnd = {
            elementId,
            relativeTime: null,
            end: false,
          };
          wasModified = true;
        } else {
          // Anchor is not in trimmed region but refers to element
          // Adjust relativeTime to preserve position relative to media time
          const mediaTimeDelta = newMediaStart - originalMediaStart;
          const currentRelativeTime =
            typeof anchorEnd === 'object' &&
            'elementId' in anchorEnd &&
            anchorEnd.relativeTime !== null
              ? anchorEnd.relativeTime
              : endMediaTime - originalMediaStart;
          const newRelativeTime = currentRelativeTime - mediaTimeDelta;

          newAnchorEnd = {
            elementId,
            relativeTime: newRelativeTime,
            end:
              typeof anchorEnd === 'object' &&
              'elementId' in anchorEnd &&
              anchorEnd.end,
          };
          wasModified = true;
        }
      }

      if (wasModified) {
        const modifiedScene = new Scene({
          id: scene.id,
          anchorStart: newAnchorStart,
          anchorEnd: newAnchorEnd,
          layout: scene.layout.clone(),
        });
        modifiedScenes.push(modifiedScene);
      } else {
        unchangedScenes.push(scene);
      }
    }

    // Apply changes to timeline

    const newScenes = [...unchangedScenes, ...modifiedScenes];
    timeline.setScenes(this.sortScenes(newScenes, timeline));

    // Validate and adjust scenes (close gaps, remove too short scenes)
    this.validateAndAdjustScenes(timeline);

    return { modifiedScenes, removedScenes, unchangedScenes };
  }

  /**
   * Adjusts scenes after trimming from the end of an element.
   *
   * Algorithm:
   * 1. For each scene, check if anchors reference the trimmed element
   * 2. Calculate media time that each anchor points to
   * 3. If anchor points to trimmed region (> newMediaEnd), adjust it
   * 4. If both anchors in trimmed region, remove scene
   *
   * @param timeline The timeline (element already modified)
   * @param trimInfo Pre-captured information about the trim
   * @returns Result containing modified, removed, and unchanged scenes
   */
  adjustScenesAfterTrimEnd(
    timeline: Timeline,
    trimInfo: TrimEndInfo,
  ): TrimAdjustmentResult {
    const { elementId, originalMediaStart, originalMediaEnd, newMediaEnd } =
      trimInfo;
    const trimmedRegionStart = newMediaEnd;
    const trimmedRegionEnd = originalMediaEnd;

    const modifiedScenes: Scene[] = [];
    const removedScenes: Scene[] = [];
    const unchangedScenes: Scene[] = [];

    for (const scene of timeline.scenes) {
      const { anchorStart, anchorEnd } = scene;

      // Get media times for anchors (using original element state)
      const startMediaTime = this.getAnchorMediaTime(
        anchorStart,
        elementId,
        originalMediaStart,
        originalMediaEnd,
      );
      const endMediaTime = this.getAnchorMediaTime(
        anchorEnd,
        elementId,
        originalMediaStart,
        originalMediaEnd,
      );

      // If neither anchor refers to this element, skip
      if (startMediaTime === null && endMediaTime === null) {
        unchangedScenes.push(scene);
        continue;
      }

      // Check if anchors are in trimmed region
      const startInTrimmed =
        startMediaTime !== null &&
        startMediaTime > trimmedRegionStart &&
        startMediaTime <= trimmedRegionEnd;

      const endInTrimmed =
        endMediaTime !== null &&
        endMediaTime > trimmedRegionStart &&
        endMediaTime <= trimmedRegionEnd;

      // If both anchors in trimmed region, remove scene
      if (startInTrimmed && endInTrimmed) {
        removedScenes.push(scene);
        continue;
      }

      // Adjust anchors
      let newAnchorStart = anchorStart;
      let newAnchorEnd = anchorEnd;
      let wasModified = false;

      // Handle start anchor
      if (startMediaTime !== null) {
        if (startInTrimmed) {
          // Anchor is in trimmed region - move to new element end
          newAnchorStart = {
            elementId,
            relativeTime: null,
            end: true,
          };
          wasModified = true;
        }
        // Note: If anchor is not in trimmed region, no adjustment needed
        // because media start hasn't changed (only media end changed)
      }

      // Handle end anchor
      if (endMediaTime !== null) {
        if (endInTrimmed) {
          // Anchor is in trimmed region - move to new element end
          newAnchorEnd = {
            elementId,
            relativeTime: null,
            end: true,
          };
          wasModified = true;
        }
        // Note: If anchor is not in trimmed region, no adjustment needed
        // because media start hasn't changed (only media end changed)
      }

      if (wasModified) {
        const modifiedScene = new Scene({
          id: scene.id,
          anchorStart: newAnchorStart,
          anchorEnd: newAnchorEnd,
          layout: scene.layout.clone(),
        });
        modifiedScenes.push(modifiedScene);
      } else {
        unchangedScenes.push(scene);
      }
    }

    // Apply changes to timeline
    const newScenes = [...unchangedScenes, ...modifiedScenes];
    timeline.setScenes(this.sortScenes(newScenes, timeline));

    // Validate and adjust scenes (close gaps, remove too short scenes)
    this.validateAndAdjustScenes(timeline);

    return { modifiedScenes, removedScenes, unchangedScenes };
  }

  /**
   * Adjusts scenes after extending from the start of an element (un-trimming start).
   *
   * Algorithm:
   * 1. For each scene, check if anchors reference the extended element
   * 2. Adjust relativeTime to preserve anchor position relative to media time
   * 3. Since media start moved earlier, add the delta to relativeTime
   *
   * Note: Extending end requires no adjustment as anchor positions remain valid.
   *
   * @param timeline The timeline (element already extended)
   * @param extendInfo Pre-captured information about the extend
   * @returns Result containing modified, removed, and unchanged scenes
   */
  adjustScenesAfterExtendStart(
    timeline: Timeline,
    extendInfo: ExtendStartInfo,
  ): TrimAdjustmentResult {
    const { elementId, originalMediaStart, originalMediaEnd, newMediaStart } =
      extendInfo;

    const modifiedScenes: Scene[] = [];
    const removedScenes: Scene[] = [];
    const unchangedScenes: Scene[] = [];

    for (const scene of timeline.scenes) {
      const { anchorStart, anchorEnd } = scene;

      // Get media times for anchors (using original element state)
      const startMediaTime = this.getAnchorMediaTime(
        anchorStart,
        elementId,
        originalMediaStart,
        originalMediaEnd,
      );
      const endMediaTime = this.getAnchorMediaTime(
        anchorEnd,
        elementId,
        originalMediaStart,
        originalMediaEnd,
      );

      // If neither anchor refers to this element, skip
      if (startMediaTime === null && endMediaTime === null) {
        unchangedScenes.push(scene);
        continue;
      }

      // Calculate media time delta (negative since newMediaStart < originalMediaStart)
      const mediaTimeDelta = newMediaStart - originalMediaStart;

      // Adjust anchors
      let newAnchorStart = anchorStart;
      let newAnchorEnd = anchorEnd;
      let wasModified = false;

      // Handle start anchor
      if (startMediaTime !== null) {
        // Adjust relativeTime to preserve position relative to media time
        const currentRelativeTime =
          typeof anchorStart === 'object' &&
          'elementId' in anchorStart &&
          anchorStart.relativeTime !== null
            ? anchorStart.relativeTime
            : null; // preserve null (keep anchored to element start)
        const newRelativeTime = currentRelativeTime
          ? currentRelativeTime - mediaTimeDelta
          : null;

        newAnchorStart = {
          elementId,
          relativeTime: newRelativeTime,
          end:
            typeof anchorStart === 'object' &&
            'elementId' in anchorStart &&
            anchorStart.end,
        };
        wasModified = true;
      }

      // Handle end anchor
      if (endMediaTime !== null) {
        // Adjust relativeTime to preserve position relative to media time
        const currentRelativeTime =
          typeof anchorEnd === 'object' &&
          'elementId' in anchorEnd &&
          anchorEnd.relativeTime !== null
            ? anchorEnd.relativeTime
            : endMediaTime - originalMediaStart;
        const newRelativeTime = currentRelativeTime - mediaTimeDelta;

        newAnchorEnd = {
          elementId,
          relativeTime: newRelativeTime,
          end:
            typeof anchorEnd === 'object' &&
            'elementId' in anchorEnd &&
            anchorEnd.end,
        };
        wasModified = true;
      }

      if (wasModified) {
        const modifiedScene = new Scene({
          id: scene.id,
          anchorStart: newAnchorStart,
          anchorEnd: newAnchorEnd,
          layout: scene.layout.clone(),
        });
        modifiedScenes.push(modifiedScene);
      } else {
        unchangedScenes.push(scene);
      }
    }

    // Apply changes to timeline
    const newScenes = [...unchangedScenes, ...modifiedScenes];
    timeline.setScenes(this.sortScenes(newScenes, timeline));

    // Validate and adjust scenes (close gaps, remove too short scenes)
    this.validateAndAdjustScenes(timeline);

    return { modifiedScenes, removedScenes, unchangedScenes };
  }

  /**
   * Adjusts scenes after merging two elements into one.
   *
   * Algorithm:
   * 1. For each scene, check if anchors reference either of the merged elements
   * 2. Re-attach anchors to the new merged element
   * 3. Adjust relativeTime to preserve anchor position relative to media time
   *    - First element: relativeTime stays the same (merged element has same media start)
   *    - Second element: adjust relativeTime = old relativeTime + (secondMediaStart - mergedMediaStart)
   *
   * @param timeline The timeline (elements already merged)
   * @param mergeInfo Pre-captured information about the merge
   * @returns Result containing modified, removed, and unchanged scenes
   */
  adjustScenesAfterMerge(
    timeline: Timeline,
    mergeInfo: MergeInfo,
  ): TrimAdjustmentResult {
    const {
      firstElementId,
      firstMediaStart,
      firstMediaEnd,
      secondElementId,
      secondMediaStart,
      secondMediaEnd,
      mergedElementId,
      mergedMediaStart,
      mergedMediaEnd,
    } = mergeInfo;

    const modifiedScenes: Scene[] = [];
    const removedScenes: Scene[] = [];
    const unchangedScenes: Scene[] = [];

    for (const scene of timeline.scenes) {
      const { anchorStart, anchorEnd } = scene;

      // Check if anchors reference either merged element
      const startRefersToFirst = this.anchorRefersToElement(
        anchorStart,
        firstElementId,
      );
      const startRefersToSecond = this.anchorRefersToElement(
        anchorStart,
        secondElementId,
      );
      const endRefersToFirst = this.anchorRefersToElement(
        anchorEnd,
        firstElementId,
      );
      const endRefersToSecond = this.anchorRefersToElement(
        anchorEnd,
        secondElementId,
      );

      // If neither anchor refers to merged elements, skip
      if (
        !startRefersToFirst &&
        !startRefersToSecond &&
        !endRefersToFirst &&
        !endRefersToSecond
      ) {
        unchangedScenes.push(scene);
        continue;
      }

      let newAnchorStart = anchorStart;
      let newAnchorEnd = anchorEnd;
      let wasModified = false;

      // Handle start anchor
      if (startRefersToFirst) {
        // Anchor was on first element - reattach to merged element
        // Must calculate actual media time and convert to relative time in merged element
        // because {relativeTime: null, end: true} on first element != end of merged element
        const startMediaTime = this.getAnchorMediaTime(
          anchorStart,
          firstElementId,
          firstMediaStart,
          firstMediaEnd,
        );
        if (startMediaTime !== null) {
          const newRelativeTime = startMediaTime - mergedMediaStart;
          newAnchorStart = {
            elementId: mergedElementId,
            relativeTime: newRelativeTime > 0 ? newRelativeTime : null,
            end: false,
          };
          wasModified = true;
        }
      } else if (startRefersToSecond) {
        // Anchor was on second element - reattach to merged element
        // Adjust relativeTime for the offset between second and merged media starts
        const startMediaTime = this.getAnchorMediaTime(
          anchorStart,
          secondElementId,
          secondMediaStart,
          secondMediaEnd,
        );
        if (startMediaTime !== null) {
          const newRelativeTime = startMediaTime - mergedMediaStart;
          const isEnd =
            typeof anchorStart === 'object' &&
            'elementId' in anchorStart &&
            anchorStart.end;

          newAnchorStart = {
            elementId: mergedElementId,
            relativeTime: newRelativeTime,
            end: isEnd,
          };
          wasModified = true;
        }
      }

      // Handle end anchor
      if (endRefersToFirst) {
        // Anchor was on first element - reattach to merged element
        // Must calculate actual media time and convert to relative time in merged element
        // because {relativeTime: null, end: true} on first element != end of merged element
        const endMediaTime = this.getAnchorMediaTime(
          anchorEnd,
          firstElementId,
          firstMediaStart,
          firstMediaEnd,
        );
        if (endMediaTime !== null) {
          const newRelativeTime = endMediaTime - mergedMediaStart;
          // Check if this is at the end of the merged element
          const isAtMergedEnd =
            Math.abs(newRelativeTime - (mergedMediaEnd - mergedMediaStart)) <
            0.0001;
          newAnchorEnd = {
            elementId: mergedElementId,
            relativeTime: isAtMergedEnd ? null : newRelativeTime,
            end: isAtMergedEnd,
          };
          wasModified = true;
        }
      } else if (endRefersToSecond) {
        // Anchor was on second element - reattach to merged element
        const endMediaTime = this.getAnchorMediaTime(
          anchorEnd,
          secondElementId,
          secondMediaStart,
          secondMediaEnd,
        );
        if (endMediaTime !== null) {
          const newRelativeTime = endMediaTime - mergedMediaStart;
          const isEnd =
            typeof anchorEnd === 'object' &&
            'elementId' in anchorEnd &&
            anchorEnd.end;

          newAnchorEnd = {
            elementId: mergedElementId,
            relativeTime: newRelativeTime,
            end: isEnd,
          };
          wasModified = true;
        }
      }

      if (wasModified) {
        const modifiedScene = new Scene({
          id: scene.id,
          anchorStart: newAnchorStart,
          anchorEnd: newAnchorEnd,
          layout: scene.layout.clone(),
        });
        modifiedScenes.push(modifiedScene);
      } else {
        unchangedScenes.push(scene);
      }
    }

    // Apply changes to timeline
    const newScenes = [...unchangedScenes, ...modifiedScenes];
    timeline.setScenes(this.sortScenes(newScenes, timeline));

    // Validate and adjust scenes (close gaps, remove too short scenes)
    this.validateAndAdjustScenes(timeline);

    return { modifiedScenes, removedScenes, unchangedScenes };
  }

  /**
   * Adjusts scenes after deleting an element.
   *
   * Rules:
   * - If anchor points to deleted element:
   *   - Prefer adjusting to end of previous element
   *   - If no previous element, adjust start anchor to start of next element
   *   - If no adjacent elements, scene must be removed
   * - If both anchors point to deleted element and no way to adjust both, remove scene
   *
   * @param timeline The timeline (element already deleted)
   * @param deleteInfo Pre-captured information about the deletion
   * @returns Result containing modified, removed, and unchanged scenes
   */
  adjustScenesAfterDelete(
    timeline: Timeline,
    deleteInfo: DeleteInfo,
  ): TrimAdjustmentResult {
    const { deletedElementId, previousElementId, nextElementId } = deleteInfo;

    const modifiedScenes: Scene[] = [];
    const removedScenes: Scene[] = [];
    const unchangedScenes: Scene[] = [];

    for (const scene of timeline.scenes) {
      const { anchorStart, anchorEnd } = scene;

      // Check if anchors reference the deleted element
      const startRefersToDeleted = this.anchorRefersToElement(
        anchorStart,
        deletedElementId,
      );
      const endRefersToDeleted = this.anchorRefersToElement(
        anchorEnd,
        deletedElementId,
      );

      // If neither anchor refers to deleted element, skip
      if (!startRefersToDeleted && !endRefersToDeleted) {
        unchangedScenes.push(scene);
        continue;
      }

      if (startRefersToDeleted && endRefersToDeleted) {
        removedScenes.push(scene);
        continue;
      }

      // Determine if we can adjust anchors
      let newAnchorStart: AnchorType | null = anchorStart;
      let newAnchorEnd: AnchorType | null = anchorEnd;
      let canAdjust = true;

      if (startRefersToDeleted) {
        if (previousElementId) {
          // Adjust to end of previous element
          newAnchorStart = {
            elementId: previousElementId,
            relativeTime: null,
            end: true,
          };
        } else if (nextElementId) {
          // No previous element, adjust to start of next element
          newAnchorStart = {
            elementId: nextElementId,
            relativeTime: null,
            end: false,
          };
        } else {
          // No adjacent elements
          canAdjust = false;
        }
      }

      if (endRefersToDeleted) {
        if (previousElementId) {
          // Adjust to end of previous element
          newAnchorEnd = {
            elementId: previousElementId,
            relativeTime: null,
            end: true,
          };
        } else {
          // Cannot adjust end anchor without previous element
          canAdjust = false;
        }
      }

      // If both anchors can't be adjusted, remove scene
      if (!canAdjust) {
        removedScenes.push(scene);
        continue;
      }

      // Apply modifications
      if (newAnchorStart !== anchorStart || newAnchorEnd !== anchorEnd) {
        const modifiedScene = new Scene({
          id: scene.id,
          anchorStart: newAnchorStart!,
          anchorEnd: newAnchorEnd!,
          layout: scene.layout.clone(),
        });
        modifiedScenes.push(modifiedScene);
      } else {
        unchangedScenes.push(scene);
      }
    }

    // Apply changes to timeline
    const newScenes = [...unchangedScenes, ...modifiedScenes];
    timeline.setScenes(this.sortScenes(newScenes, timeline));

    // Validate and adjust scenes (close gaps, remove too short scenes)
    this.validateAndAdjustScenes(timeline);

    return { modifiedScenes, removedScenes, unchangedScenes };
  }

  /**
   * Adjusts scenes after excising (cutting from middle) an element.
   *
   * The original element is split into left and right parts with a section excised from the middle.
   *
   * Rules:
   * - Scene fully contains excision range: Re-attach anchors to left and right elements, adjusting relativeTime
   *   - Left element has same media start, so anchor relativeTime stays the same
   *   - Right element anchor needs adjustment: relativeTimeNew = relativeTimeOld - (newMediaStartTime - oldMediaStartTime)
   * - Excision overlaps start or end of scene: Re-attach anchor to end of left element
   * - One or both anchors point to preserved parts: Adjust to new elements, preserving media time position
   * - Both anchors point to excised section: Remove scene
   *
   * @param timeline The timeline (element already split)
   * @param exciseInfo Pre-captured information about the excision
   * @returns Result containing modified, removed, and unchanged scenes
   */
  adjustScenesAfterExcise(
    timeline: Timeline,
    exciseInfo: ExciseInfo,
  ): TrimAdjustmentResult {
    const {
      originalElementId,
      originalMediaStart,
      originalMediaEnd,
      excisionMediaStart,
      excisionMediaEnd,
      leftElementId,
      rightElementId,
      leftMediaEnd,
      rightMediaStart,
    } = exciseInfo;

    const modifiedScenes: Scene[] = [];
    const removedScenes: Scene[] = [];
    const unchangedScenes: Scene[] = [];

    for (const scene of timeline.scenes) {
      const { anchorStart, anchorEnd } = scene;

      // Check if anchors reference the original element
      const startRefersToOriginal = this.anchorRefersToElement(
        anchorStart,
        originalElementId,
      );
      const endRefersToOriginal = this.anchorRefersToElement(
        anchorEnd,
        originalElementId,
      );

      // If neither anchor refers to original element, skip
      if (!startRefersToOriginal && !endRefersToOriginal) {
        unchangedScenes.push(scene);
        continue;
      }

      // Get media times of anchors relative to original element
      const startMediaTime = startRefersToOriginal
        ? this.getAnchorMediaTime(
            anchorStart,
            originalElementId,
            originalMediaStart,
            originalMediaEnd,
          )
        : null;
      const endMediaTime = endRefersToOriginal
        ? this.getAnchorMediaTime(
            anchorEnd,
            originalElementId,
            originalMediaStart,
            originalMediaEnd,
          )
        : null;

      // Determine which part each anchor belongs to
      const startInLeftPart =
        startMediaTime !== null && startMediaTime <= leftMediaEnd;
      const startInRightPart =
        startMediaTime !== null && startMediaTime >= rightMediaStart;
      const startInExcisedPart =
        startMediaTime !== null &&
        startMediaTime >= excisionMediaStart &&
        startMediaTime <= excisionMediaEnd;

      const endInLeftPart =
        endMediaTime !== null && endMediaTime <= leftMediaEnd;
      const endInRightPart =
        endMediaTime !== null && endMediaTime >= rightMediaStart;
      const endInExcisedPart =
        endMediaTime !== null &&
        endMediaTime >= excisionMediaStart &&
        endMediaTime <= excisionMediaEnd;

      // Case 1: Both anchors in excised part -> remove scene
      if (startInExcisedPart && endInExcisedPart) {
        removedScenes.push(scene);
        continue;
      }

      let newAnchorStart: AnchorType = anchorStart;
      let newAnchorEnd: AnchorType = anchorEnd;
      let wasModified = false;

      // Handle start anchor
      if (startRefersToOriginal) {
        if (startInLeftPart) {
          // Anchor is in left part - adjust to left element
          // relativeTime stays the same because left element has same media start
          const relativeTime =
            typeof anchorStart === 'object' &&
            'elementId' in anchorStart &&
            anchorStart.relativeTime !== null
              ? anchorStart.relativeTime
              : null;
          const isEnd =
            typeof anchorStart === 'object' &&
            'elementId' in anchorStart &&
            anchorStart.end;

          newAnchorStart = {
            elementId: leftElementId,
            relativeTime,
            end: isEnd,
          };
          wasModified = true;
        } else if (startInRightPart) {
          // Anchor is in right part - adjust to right element
          // Adjust relativeTime for new media start
          const relativeTime =
            typeof anchorStart === 'object' &&
            'elementId' in anchorStart &&
            anchorStart.relativeTime !== null
              ? anchorStart.relativeTime -
                (rightMediaStart - originalMediaStart)
              : null;
          const isEnd =
            typeof anchorStart === 'object' &&
            'elementId' in anchorStart &&
            anchorStart.end;

          newAnchorStart = {
            elementId: rightElementId,
            relativeTime,
            end: isEnd,
          };
          wasModified = true;
        } else {
          // Anchor is in excised part - adjust to end of left element
          newAnchorStart = {
            elementId: leftElementId,
            relativeTime: null,
            end: true,
          };
          wasModified = true;
        }
      }

      // Handle end anchor
      if (endRefersToOriginal) {
        if (endInLeftPart) {
          // Anchor is in left part - adjust to left element
          const relativeTime =
            typeof anchorEnd === 'object' &&
            'elementId' in anchorEnd &&
            anchorEnd.relativeTime !== null
              ? anchorEnd.relativeTime
              : null;
          const isEnd =
            typeof anchorEnd === 'object' &&
            'elementId' in anchorEnd &&
            anchorEnd.end;

          newAnchorEnd = {
            elementId: leftElementId,
            relativeTime,
            end: isEnd,
          };
          wasModified = true;
        } else if (endInRightPart) {
          // Anchor is in right part - adjust to right element
          const relativeTime =
            typeof anchorEnd === 'object' &&
            'elementId' in anchorEnd &&
            anchorEnd.relativeTime !== null
              ? anchorEnd.relativeTime - (rightMediaStart - originalMediaStart)
              : null;
          const isEnd =
            typeof anchorEnd === 'object' &&
            'elementId' in anchorEnd &&
            anchorEnd.end;

          newAnchorEnd = {
            elementId: rightElementId,
            relativeTime,
            end: isEnd,
          };
          wasModified = true;
        } else {
          // Anchor is in excised part - adjust to end of left element
          newAnchorEnd = {
            elementId: leftElementId,
            relativeTime: null,
            end: true,
          };
          wasModified = true;
        }
      }

      if (wasModified) {
        const modifiedScene = new Scene({
          id: scene.id,
          anchorStart: newAnchorStart,
          anchorEnd: newAnchorEnd,
          layout: scene.layout.clone(),
        });
        modifiedScenes.push(modifiedScene);
      } else {
        unchangedScenes.push(scene);
      }
    }

    // Apply changes to timeline
    const newScenes = [...unchangedScenes, ...modifiedScenes];
    timeline.setScenes(this.sortScenes(newScenes, timeline));

    // Validate and adjust scenes (close gaps, remove too short scenes)
    this.validateAndAdjustScenes(timeline);

    return { modifiedScenes, removedScenes, unchangedScenes };
  }

  /**
   * Adjusts scenes after splitting an element into two parts.
   *
   * The original element is split at a point into left and right parts.
   *
   * Rules:
   * - Anchors pointing to the original element are re-attached to left or right element
   * - If anchor is before split point, attach to left element (same media start, so relativeTime stays same)
   * - If anchor is at or after split point, attach to right element (adjust relativeTime for new media start)
   * - Preserve anchor position relative to media time
   *
   * @param timeline The timeline (element already split)
   * @param splitInfo Pre-captured information about the split
   * @returns Result containing modified, removed, and unchanged scenes
   */
  adjustScenesAfterSplit(
    timeline: Timeline,
    splitInfo: SplitInfo,
  ): TrimAdjustmentResult {
    const {
      originalElementId,
      originalMediaStart,
      originalMediaEnd,
      splitMediaTime,
      leftElementId,
      rightElementId,
    } = splitInfo;

    const modifiedScenes: Scene[] = [];
    const removedScenes: Scene[] = [];
    const unchangedScenes: Scene[] = [];

    for (const scene of timeline.scenes) {
      const { anchorStart, anchorEnd } = scene;

      // Check if anchors reference the original element
      const startRefersToOriginal = this.anchorRefersToElement(
        anchorStart,
        originalElementId,
      );
      const endRefersToOriginal = this.anchorRefersToElement(
        anchorEnd,
        originalElementId,
      );

      // If neither anchor refers to original element, skip
      if (!startRefersToOriginal && !endRefersToOriginal) {
        unchangedScenes.push(scene);
        continue;
      }

      let newAnchorStart: AnchorType = anchorStart;
      let newAnchorEnd: AnchorType = anchorEnd;
      let wasModified = false;

      // Handle start anchor
      if (startRefersToOriginal) {
        // Get media time of the anchor
        const anchorMediaTime = this.getAnchorMediaTime(
          anchorStart,
          originalElementId,
          originalMediaStart,
          originalMediaEnd,
        );

        if (anchorMediaTime !== null) {
          if (anchorMediaTime < splitMediaTime) {
            // Anchor is in left part - attach to left element
            // relativeTime stays the same because left element has same media start
            const relativeTime =
              typeof anchorStart === 'object' &&
              'elementId' in anchorStart &&
              anchorStart.relativeTime !== null
                ? anchorStart.relativeTime
                : null;
            const isEnd =
              typeof anchorStart === 'object' &&
              'elementId' in anchorStart &&
              anchorStart.end;

            newAnchorStart = {
              elementId: leftElementId,
              relativeTime,
              end: isEnd,
            };
            wasModified = true;
          } else {
            // Anchor is at or after split point - attach to right element
            // Adjust relativeTime for new media start
            const relativeTime =
              typeof anchorStart === 'object' &&
              'elementId' in anchorStart &&
              anchorStart.relativeTime !== null
                ? anchorStart.relativeTime -
                  (splitMediaTime - originalMediaStart)
                : null;
            const isEnd =
              typeof anchorStart === 'object' &&
              'elementId' in anchorStart &&
              anchorStart.end;

            newAnchorStart = {
              elementId: rightElementId,
              relativeTime,
              end: isEnd,
            };
            wasModified = true;
          }
        }
      }

      // Handle end anchor
      if (endRefersToOriginal) {
        // Get media time of the anchor
        const anchorMediaTime = this.getAnchorMediaTime(
          anchorEnd,
          originalElementId,
          originalMediaStart,
          originalMediaEnd,
        );

        if (anchorMediaTime !== null) {
          if (anchorMediaTime < splitMediaTime) {
            // Anchor is in left part - attach to left element
            const relativeTime =
              typeof anchorEnd === 'object' &&
              'elementId' in anchorEnd &&
              anchorEnd.relativeTime !== null
                ? anchorEnd.relativeTime
                : null;
            const isEnd =
              typeof anchorEnd === 'object' &&
              'elementId' in anchorEnd &&
              anchorEnd.end;

            newAnchorEnd = {
              elementId: leftElementId,
              relativeTime,
              end: isEnd,
            };
            wasModified = true;
          } else {
            // Anchor is at or after split point - attach to right element
            const relativeTime =
              typeof anchorEnd === 'object' &&
              'elementId' in anchorEnd &&
              anchorEnd.relativeTime !== null
                ? anchorEnd.relativeTime - (splitMediaTime - originalMediaStart)
                : null;
            const isEnd =
              typeof anchorEnd === 'object' &&
              'elementId' in anchorEnd &&
              anchorEnd.end;

            newAnchorEnd = {
              elementId: rightElementId,
              relativeTime,
              end: isEnd,
            };
            wasModified = true;
          }
        }
      }

      if (wasModified) {
        const modifiedScene = new Scene({
          id: scene.id,
          anchorStart: newAnchorStart,
          anchorEnd: newAnchorEnd,
          layout: scene.layout.clone(),
        });
        modifiedScenes.push(modifiedScene);
      } else {
        unchangedScenes.push(scene);
      }
    }

    // Apply changes to timeline
    const newScenes = [...unchangedScenes, ...modifiedScenes];
    timeline.setScenes(this.sortScenes(newScenes, timeline));

    // Validate and adjust scenes (close gaps, remove too short scenes)
    this.validateAndAdjustScenes(timeline);

    return { modifiedScenes, removedScenes, unchangedScenes };
  }

  /**
   * Validates and adjusts scenes after any modification operation.
   *
   * Applies the following rules:
   * 1. Filters out scenes with duration less than minimum (0.1 second)
   * 2. Closes gaps between consecutive scenes by adjusting start anchor to match previous end
   * 3. Resolves overlaps by adjusting start anchor to match previous end
   *
   * @param timeline The timeline with scenes to validate
   * @param minDuration Minimum scene duration in seconds (default: 0.1)
   * @returns Result containing modified, removed, and unchanged scenes
   */
  validateAndAdjustScenes(timeline: Timeline, minDuration: number = 0.1) {
    // Sort scenes by start time
    let sortedScenes = this.sortScenes([...timeline.scenes], timeline);
    const transcriptTrackElements =
      timeline.tracks.find(t => t instanceof TranscriptTrack)?.elements || null;
    const transcriptTrackElementIds = new Set(
      transcriptTrackElements ? transcriptTrackElements.map(e => e.id) : [],
    );

    if (!transcriptTrackElements?.length) {
      logger.warn(
        'No transcript track found in timeline. Scenes validation and adjustment skipped.',
      );
      return;
    }

    // filter out scenes that reference non-transcript-track elements
    // current limitation is that scenes can only reference transcript track elements
    sortedScenes = sortedScenes.filter(scene => {
      const anchorStartId = this.getElementIdFromAnchor(scene.anchorStart);
      const anchorEndId = this.getElementIdFromAnchor(scene.anchorEnd);

      if (anchorStartId && !transcriptTrackElementIds.has(anchorStartId)) {
        return false;
      }
      if (anchorEndId && !transcriptTrackElementIds.has(anchorEndId)) {
        return false;
      }
      return true;
    });

    // Rule 1: Filter out scenes with duration less than minimum
    const { validScenes } = this.filterShortScenes(
      sortedScenes,
      timeline,
      minDuration,
    );

    // Rule 2 & 3: Close gaps and resolve overlaps
    const adjustedScenes = this.closeGapsAndResolveOverlaps(
      validScenes,
      timeline,
    );

    // Preserve the end anchor of the last removed scene if it was the final scene
    const finalScenes = this.adjustFirstAndLastScenesAnchors(
      adjustedScenes,
      transcriptTrackElements[0].id,
      transcriptTrackElements[transcriptTrackElements.length - 1].id,
    );

    // Apply changes to timeline
    const sortedFinalScenes = this.sortScenes(finalScenes, timeline);
    timeline.setScenes(sortedFinalScenes);
  }

  /**
   * Rule 1: Filter out scenes with duration less than minimum.
   * Short scenes are allowed if timeline duration is zero or if the scene uses clip anchors.
   *
   * @returns Valid scenes and the last removed scene if it was the final scene in the list
   */
  private filterShortScenes(
    scenes: Scene[],
    timeline: Timeline,
    minDuration: number,
  ): { validScenes: Scene[]; lastRemovedScene: Scene | null } {
    const validScenes: Scene[] = [];
    let lastRemovedScene: Scene | null = null;
    const timelineDuration = timeline.duration;

    for (let i = 0; i < scenes.length; i++) {
      const scene = scenes[i];
      const startTime = this.anchorResolver.resolve(
        scene.anchorStart,
        timeline,
      );
      const endTime = this.anchorResolver.resolve(scene.anchorEnd, timeline);
      const duration = endTime - startTime;

      const allowShortScene =
        timelineDuration <= 0 ||
        this.isClipAnchor(scene.anchorStart) ||
        this.isClipAnchor(scene.anchorEnd);

      if (duration < minDuration && !allowShortScene) {
        // Track if this is the last scene being removed
        if (i === scenes.length - 1) {
          lastRemovedScene = scene;
        }
        continue;
      }

      validScenes.push(scene);
    }

    return { validScenes, lastRemovedScene };
  }

  /**
   * Rules 2 & 3: Close gaps and resolve overlaps between consecutive scenes.
   * Each scene's start anchor is adjusted to match the previous scene's end anchor if they don't align.
   * Scenes that become invalid (start > end) after adjustment are filtered out.
   *
   * Adjustment rules:
   * - Always adjust if previous scene ends at 'clip-start'
   * - Adjust if both anchors are element anchors and they don't match
   * - Never adjust if previous scene ends at 'clip-end' (unless current also starts at 'clip-end')
   *
   * @returns Adjusted scenes with gaps closed and overlaps resolved
   */
  private closeGapsAndResolveOverlaps(
    scenes: Scene[],
    timeline: Timeline,
  ): Scene[] {
    if (scenes.length === 0) {
      return [];
    }

    const adjustedScenes: Scene[] = [];
    let previousSceneEnd: AnchorType | null = null;

    for (const scene of scenes) {
      let newAnchorStart = scene.anchorStart;

      // Check if we need to adjust the start anchor to match previous scene's end
      // Only adjust when:
      // 1. Previous scene ends at 'clip-start', OR
      // 2. Both anchors are element anchors and they're not equal
      if (
        previousSceneEnd !== null &&
        (previousSceneEnd === 'clip-start' ||
          (typeof newAnchorStart !== 'string' &&
            typeof previousSceneEnd !== 'string' &&
            !this.areAnchorsEqual(newAnchorStart, previousSceneEnd)))
      ) {
        newAnchorStart = previousSceneEnd;
      }

      // Verify the adjusted scene is still valid (start <= end)
      const newStartTime = this.anchorResolver.resolve(
        newAnchorStart,
        timeline,
      );
      const endTime = this.anchorResolver.resolve(scene.anchorEnd, timeline);

      if (newStartTime > endTime) {
        // Scene became invalid after adjustment, skip it
        continue;
      }

      // Create adjusted scene if anchor changed, otherwise use original
      const adjustedScene: Scene = this.areAnchorsEqual(
        newAnchorStart,
        scene.anchorStart,
      )
        ? scene
        : new Scene({
            id: scene.id,
            anchorStart: newAnchorStart,
            anchorEnd: scene.anchorEnd,
            layout: scene.layout.clone(),
          });

      adjustedScenes.push(adjustedScene);
      previousSceneEnd = adjustedScene.anchorEnd;
    }

    return adjustedScenes;
  }

  /**
   * Preserves the end anchor from the last removed scene if it was the final scene.
   * This ensures the timeline maintains its original end boundary.
   *
   * @returns Scenes with potentially adjusted last scene
   */
  private adjustFirstAndLastScenesAnchors(
    scenes: Scene[],
    firstElementId: string,
    lastElementId: string,
  ): Scene[] {
    if (scenes.length === 0) {
      return [];
    }
    const adjustedScenes = [...scenes];
    const firstScene = adjustedScenes[0];
    const firstSceneAnchorStart = firstScene.anchorStart;

    // Adjust first scene start anchor if it references a deleted element
    if (
      firstSceneAnchorStart !== 'clip-start' &&
      !this.areAnchorsEqual(firstSceneAnchorStart, {
        elementId: firstElementId,
        relativeTime: null,
        end: false,
      })
    ) {
      adjustedScenes[0] = new Scene({
        id: firstScene.id,
        anchorStart: {
          elementId: firstElementId,
          relativeTime: null,
          end: false,
        },
        anchorEnd: firstScene.anchorEnd,
        layout: firstScene.layout.clone(),
      });
    }

    const lastScene = adjustedScenes[adjustedScenes.length - 1];
    const lastSceneAnchorEnd = lastScene.anchorEnd;

    // Adjust last scene end anchor if it references a deleted element
    if (
      lastSceneAnchorEnd !== 'clip-end' &&
      !this.areAnchorsEqual(lastSceneAnchorEnd, {
        elementId: lastElementId,
        relativeTime: null,
        end: true,
      })
    ) {
      adjustedScenes[adjustedScenes.length - 1] = new Scene({
        id: lastScene.id,
        anchorStart: lastScene.anchorStart,
        anchorEnd: {
          elementId: lastElementId,
          relativeTime: null,
          end: true,
        },
        layout: lastScene.layout.clone(),
      });
    }

    return adjustedScenes;
  }

  /**
   * Checks if two anchors are equal.
   */
  private areAnchorsEqual(anchor1: AnchorType, anchor2: AnchorType): boolean {
    // Handle clip anchors
    if (typeof anchor1 === 'string' || typeof anchor2 === 'string') {
      return anchor1 === anchor2;
    }

    // Compare element anchors
    return (
      anchor1.elementId === anchor2.elementId &&
      anchor1.end === anchor2.end &&
      anchor1.relativeTime === anchor2.relativeTime
    );
  }

  // ===================================================================
  // Helper Methods
  // ===================================================================

  /**
   * Checks if an anchor refers to a specific element
   */
  private anchorRefersToElement(
    anchor: AnchorType,
    elementId: string,
  ): boolean {
    if (typeof anchor === 'string') {
      return false; // clip-start/clip-end don't refer to elements
    }
    return anchor.elementId === elementId;
  }

  /**
   * Gets the media time that an anchor points to, relative to element's media time.
   * Returns null if anchor doesn't refer to the specified element.
   *
   * @param anchor The anchor to evaluate
   * @param elementId The element ID to check
   * @param elementMediaStart The element's media start time
   * @param elementMediaEnd The element's media end time
   * @returns Media time or null if anchor doesn't refer to element
   */
  private getAnchorMediaTime(
    anchor: AnchorType,
    elementId: string,
    elementMediaStart: number,
    elementMediaEnd: number,
  ): number | null {
    // Clip anchors don't refer to elements
    if (typeof anchor === 'string') {
      return null;
    }

    // Check if anchor refers to this element
    if (anchor.elementId !== elementId) {
      return null;
    }

    // Calculate media time
    if (anchor.end) {
      // Anchored to element end
      return elementMediaEnd;
    } else if (anchor.relativeTime !== null) {
      // Anchored with relative time
      return elementMediaStart + anchor.relativeTime;
    } else {
      // Anchored to element start
      return elementMediaStart;
    }
  }

  sortScenes(scenes: Scene[], timeline: Timeline): Scene[] {
    return scenes.sort((scene1, scene2) => {
      const anchor1 = this.anchorResolver.resolve(scene1.anchorStart, timeline);
      const anchor2 = this.anchorResolver.resolve(scene2.anchorStart, timeline);
      return anchor1 - anchor2;
    });
  }

  private isClipAnchor(anchor: AnchorType): boolean {
    return anchor === 'clip-start' || anchor === 'clip-end';
  }

  private isElementAnchor(anchor: AnchorType): boolean {
    return typeof anchor === 'object' && 'elementId' in anchor;
  }

  private getElementIdFromAnchor(anchor: AnchorType): string | null {
    if (this.isElementAnchor(anchor)) {
      return (anchor as ElementAnchor).elementId;
    }
    return null;
  }

  /**
   * Creates a scene for a newly inserted element, anchored to element start and end.
   * Uses the default 'cover' layout template.
   *
   * When a crop is provided, it's used directly. Otherwise, a default full-frame crop is used.
   *
   * @param timeline The timeline to add the scene to
   * @param element The element to create a scene for (must have id property)
   * @param trackId The track ID to bind the layout to
   * @param options Optional parameters
   * @param options.crop Pre-computed crop box { x, y, w, h } (normalized 0-1 values)
   */
  createSceneForElement(
    timeline: Timeline,
    element: { id: string },
    trackId: string,
    options?: {
      crop?: { x: number; y: number; w: number; h: number };
    },
  ): void {
    // Use 'cover' as default layout template
    const templateId = mapFitTypeToTemplateId('cover');

    // Create layout segment with default template
    const layout = LayoutSegmentFactory.create(
      templateId,
      trackId,
      options?.crop,
    );

    // Create scene anchored to element start and end
    const scene = new Scene({
      anchorStart: {
        elementId: element.id,
        relativeTime: null,
        end: false,
      },
      anchorEnd: {
        elementId: element.id,
        relativeTime: null,
        end: true,
      },
      layout,
    });

    // Add scene to timeline
    const newScenes = [...timeline.scenes, scene];
    timeline.setScenes(this.sortScenes(newScenes, timeline));

    logger.info(`Created scene for element ${element.id}`);
  }

  /**
   * Adjusts scenes after elements are inserted:
   * 1. Finds scene overlapping with inserted elements
   * 2. Splits it into two parts
   * 3. Adjusts first part end anchor to end of previous element (or removes if no previous)
   * 4. Adjusts second part start anchor to start of next element (or removes if no next)
   * 5. Merges scenes from clipboard that have same scene ID
   */
  adjustScenesForInsertedElements(
    timeline: Timeline,
    insertionResult: PasteInsertionResult,
  ): void {
    const { insertedElements, targetElement, splitParts } = insertionResult;
    if (!insertedElements.length) {
      return;
    }

    const firstInserted = insertedElements[0];
    const lastInserted = insertedElements[insertedElements.length - 1];

    const insertedStartTime = firstInserted.time;
    const insertedEndTime = lastInserted.end_time;

    if (splitParts) {
      const { firstPart, secondPart } = splitParts;
      this.adjustScenesAfterSplit(timeline, {
        originalElementId: targetElement.id,
        originalMediaStart: targetElement.getMediaStartTime(),
        originalMediaEnd: targetElement.getMediaEndTime(),
        splitMediaTime: secondPart.getMediaStartTime(),
        leftElementId: firstPart.id,
        rightElementId: secondPart.id,
      });
    }

    // Find scene overlapping with inserted elements
    const overlappingScene = timeline.scenes.find(scene => {
      const sceneStart = this.anchorResolver.resolve(
        scene.anchorStart,
        timeline,
      );
      const sceneEnd = this.anchorResolver.resolve(scene.anchorEnd, timeline);

      return sceneStart < insertedEndTime && sceneEnd > insertedStartTime;
    });

    if (!overlappingScene) {
      return;
    }

    // Find previous and next elements relative to inserted range
    const track = timeline.getTrack(targetElement.track!.toString())!;

    const previousElement = track.elements
      .filter(el => el.time < insertedStartTime)
      .pop();

    const nextElement = track.elements.find(
      el => el.end_time > insertedEndTime,
    );

    // Split the overlapping scene into two parts
    // First part: original start → end of previous element (or removed if no previous)
    // Second part: start of next element → original end (or removed if no next)

    if (!previousElement && !nextElement) {
      // No adjacent elements - remove the overlapping scene entirely
      this.removeScene(timeline, overlappingScene.id);
      return;
    }

    if (previousElement && !nextElement) {
      // Only previous exists - adjust scene end to previous element end
      const endAnchor: ElementAnchor = {
        elementId: previousElement.id,
        relativeTime: null,
        end: true,
      };

      const updatedScene = new Scene({
        id: overlappingScene.id,
        anchorStart: overlappingScene.anchorStart,
        anchorEnd: endAnchor,
        layout: overlappingScene.layout,
      });

      this.updateScene(timeline, overlappingScene.id, updatedScene);
      return;
    }

    if (!previousElement && nextElement) {
      // Only next exists - adjust scene start to next element start
      const startAnchor: ElementAnchor = {
        elementId: nextElement.id,
        relativeTime: 0,
        end: false,
      };

      const updatedScene = new Scene({
        id: overlappingScene.id,
        anchorStart: startAnchor,
        anchorEnd: overlappingScene.anchorEnd,
        layout: overlappingScene.layout,
      });

      this.updateScene(timeline, overlappingScene.id, updatedScene);
      return;
    }

    if (previousElement && nextElement) {
      // Both exist - split into two scenes
      const firstSceneEndAnchor: ElementAnchor = {
        elementId: previousElement.id,
        relativeTime: null,
        end: true,
      };

      const secondSceneStartAnchor: ElementAnchor = {
        elementId: nextElement.id,
        relativeTime: 0,
        end: false,
      };

      const firstScene = new Scene({
        id: `${overlappingScene.id}_before_insert`,
        anchorStart: overlappingScene.anchorStart,
        anchorEnd: firstSceneEndAnchor,
        layout: overlappingScene.layout.clone(),
      });

      const secondScene = new Scene({
        id: `${overlappingScene.id}_after_insert`,
        anchorStart: secondSceneStartAnchor,
        anchorEnd: overlappingScene.anchorEnd,
        layout: overlappingScene.layout.clone(),
      });

      // Remove original scene and add split scenes
      this.removeScene(timeline, overlappingScene.id);
      this.addScene(timeline, firstScene);
      this.addScene(timeline, secondScene);
    }
  }

  captureScenesForElements(
    timeline: Timeline,
    elements: readonly VideoTranscriptElement[],
  ): Array<{
    sceneOverlapping: Scene;
    sceneOriginal: Scene;
  }> {
    const snapshots: {
      sceneOverlapping: Scene;
      sceneOriginal: Scene;
    }[] = [];

    for (const element of elements) {
      const elementStartTime = element.time;
      const elementEndTime = element.end_time;

      for (const scene of timeline.scenes) {
        const sceneStart = this.anchorResolver.resolve(
          scene.anchorStart,
          timeline,
        );
        const sceneEnd = this.anchorResolver.resolve(scene.anchorEnd, timeline);

        // Check if scene overlaps with removed part
        const overlaps =
          sceneStart < elementEndTime && sceneEnd > elementStartTime;

        if (!overlaps) {
          continue;
        }

        // Calculate overlap boundaries
        const overlapStart = Math.max(sceneStart, elementStartTime);
        const overlapEnd = Math.min(sceneEnd, elementEndTime);

        // Create adjusted anchors relative to removed part
        const adjustedStartAnchor = this.createRelativeAnchor(
          scene.anchorStart,
          overlapStart,
          elementStartTime,
          elementEndTime,
          element.id,
        );

        const adjustedEndAnchor = this.createRelativeAnchor(
          scene.anchorEnd,
          overlapEnd,
          elementStartTime,
          elementEndTime,
          element.id,
        );

        // Clone scene with adjusted anchors
        const adjustedScene = new Scene({
          id: scene.id,
          anchorStart: adjustedStartAnchor,
          anchorEnd: adjustedEndAnchor,
          layout: scene.layout.clone(),
        });

        snapshots.push({
          sceneOverlapping: adjustedScene,
          sceneOriginal: scene,
        });
      }
    }
    return snapshots;
  }

  /**
   * Restores scenes from snapshots by:
   * 1. Sorting snapshots by resolved anchor times
   * 2. Merging consecutive scenes with same ID together
   * 3. Assigning unique IDs to non-consecutive scenes with same original ID
   * 4. Re-anchoring merged scenes to original element if possible (no overlaps)
   * 5. Validating and adjusting final scenes
   */
  restoreScenesFromSnapshots(
    timeline: Timeline,
    snapshots: {
      sceneOverlapping: Scene;
      sceneOriginal: Scene;
    }[],
  ): void {
    if (!snapshots.length) {
      return;
    }

    // Step 1: Sort snapshots by resolved anchor times
    const sortedSnapshots = snapshots.sort((a, b) => {
      const aStart = this.anchorResolver.resolve(
        a.sceneOverlapping.anchorStart,
        timeline,
      );
      const bStart = this.anchorResolver.resolve(
        b.sceneOverlapping.anchorStart,
        timeline,
      );
      return aStart - bStart;
    });

    // Step 2: Merge consecutive scenes with same ID using reduce
    const mergedSnapshots = sortedSnapshots.reduce<
      Array<{
        sceneOverlapping: Scene;
        sceneOriginal: Scene;
      }>
    >((acc, current) => {
      const previous = acc[acc.length - 1];

      if (
        previous &&
        previous.sceneOverlapping.id === current.sceneOverlapping.id
      ) {
        // Merge consecutive scenes with same ID
        const mergedOverlapping = new Scene({
          id: previous.sceneOverlapping.id,
          anchorStart: previous.sceneOverlapping.anchorStart,
          anchorEnd: current.sceneOverlapping.anchorEnd,
          layout: previous.sceneOverlapping.layout.clone(),
        });

        acc[acc.length - 1] = {
          sceneOverlapping: mergedOverlapping,
          sceneOriginal: previous.sceneOriginal, // Keep first original scene
        };
      } else {
        // Different scene ID or not consecutive - add as new
        acc.push(current);
      }

      return acc;
    }, []);

    // Step 3: Generate unique IDs for non-consecutive scenes with same original ID
    const sceneIdCounts = new Map<string, number>();
    const mergedWithUniqueIds = mergedSnapshots.map(snapshot => {
      const originalId = snapshot.sceneOverlapping.id;
      const count = sceneIdCounts.get(originalId) || 0;
      sceneIdCounts.set(originalId, count + 1);

      // If this scene ID appeared before (non-consecutive), make it unique
      const finalId = count > 0 ? `${originalId}_${count}` : originalId;
      return {
        sceneOverlapping: new Scene({
          id: finalId,
          anchorStart: snapshot.sceneOverlapping.anchorStart,
          anchorEnd: snapshot.sceneOverlapping.anchorEnd,
          layout: snapshot.sceneOverlapping.layout.clone(),
        }),
        sceneOriginal: snapshot.sceneOriginal,
      };
    });

    // Step 4: Re-anchor to original element if possible (start and end separately)
    const restoredScenes: Scene[] = [];
    // TODO: trying to restore original anchors to close gaps between elements, as gaps were not distrubuted betweeen overlapping scenes.
    // TODO: Buggy, think about better way to handle this case or clean up the logic.
    for (const { sceneOverlapping } of mergedWithUniqueIds) {
      //   // Try to re-anchor start and end separately
      //   const { anchorStart, anchorEnd } = this.tryRestoringOriginalAnchors(
      //     timeline,
      //     sceneOverlapping,
      //     sceneOriginal,
      //     restoredScenes,
      //   );

      //   const reanchoredScene = new Scene({
      //     id: sceneOverlapping.id,
      //     anchorStart,
      //     anchorEnd,
      //     layout: sceneOverlapping.layout.clone(),
      //   });

      restoredScenes.push(sceneOverlapping);
    }

    // Step 5: Set scenes to timeline
    timeline.setScenes(restoredScenes);

    // Step 6: Validate and adjust scenes (close gaps, remove too short scenes, resolve overlaps)
    this.validateAndAdjustScenes(timeline);
  }

  /**
   * Checks if two anchors reference different elements.
   */
  private isDifferentElement(
    anchor1: AnchorType,
    anchor2: AnchorType,
  ): boolean {
    // If one is clip anchor and other is element anchor, they're different
    if (typeof anchor1 === 'string' && typeof anchor2 !== 'string') {
      return true;
    }
    if (typeof anchor1 !== 'string' && typeof anchor2 === 'string') {
      return true;
    }

    // If both are clip anchors, they're the same
    if (typeof anchor1 === 'string' && typeof anchor2 === 'string') {
      return false;
    }

    // Both are element anchors - compare element IDs
    return (
      (anchor1 as ElementAnchor).elementId !==
      (anchor2 as ElementAnchor).elementId
    );
  }

  /**
   * Checks if a single anchor can be re-anchored without causing overlaps.
   */
  private canReanchorSingleAnchor(
    timeline: Timeline,
    newAnchor: AnchorType,
    otherAnchor: AnchorType,
    existingScenes: Scene[],
    anchorType: 'start' | 'end',
  ): boolean {
    // Resolve anchor time
    const newAnchorTime = this.anchorResolver.resolve(newAnchor, timeline);

    // Check if anchor time is valid
    if (!isFinite(newAnchorTime)) {
      return false;
    }

    // Check if element exists (if it's an element anchor)
    if (typeof newAnchor !== 'string') {
      const element = timeline.getElement(newAnchor.elementId);
      if (!element) {
        return false; // Element doesn't exist
      }
    }

    // Resolve other anchor time to ensure scene is valid
    const otherAnchorTime = this.anchorResolver.resolve(otherAnchor, timeline);
    if (!isFinite(otherAnchorTime)) {
      return false;
    }

    // Ensure anchors are in correct order
    const sceneStart = anchorType === 'start' ? newAnchorTime : otherAnchorTime;
    const sceneEnd = anchorType === 'end' ? newAnchorTime : otherAnchorTime;

    if (sceneStart >= sceneEnd) {
      return false; // Invalid scene range
    }

    // Check for overlaps with existing restored scenes
    for (const existingScene of existingScenes) {
      const existingStart = this.anchorResolver.resolve(
        existingScene.anchorStart,
        timeline,
      );
      const existingEnd = this.anchorResolver.resolve(
        existingScene.anchorEnd,
        timeline,
      );

      // Check for overlap
      if (sceneStart < existingEnd && sceneEnd > existingStart) {
        return false; // Overlap detected
      }
    }

    return true;
  }

  /**
   * Captures scenes that fully or partially overlap with the removed element part.
   * Adjusts scene anchors to be relative to the trimmed/deleted/excised part.
   */
  captureOverlappingScenes(
    timeline: Timeline,
    element: VideoTranscriptElement,
    removedPartStartTime: number,
    removedPartEndTime: number,
    originalMediaStart: number,
    originalMediaEnd: number,
  ): Scene[] {
    const snapshots: Scene[] = [];

    void originalMediaStart;
    void originalMediaEnd;

    for (const scene of timeline.scenes) {
      const sceneStart = this.anchorResolver.resolve(
        scene.anchorStart,
        timeline,
      );
      const sceneEnd = this.anchorResolver.resolve(scene.anchorEnd, timeline);

      // Check if scene overlaps with removed part
      const overlaps =
        sceneStart < removedPartEndTime && sceneEnd > removedPartStartTime;

      if (!overlaps) {
        continue;
      }

      // Calculate overlap boundaries
      const overlapStart = Math.max(sceneStart, removedPartStartTime);
      const overlapEnd = Math.min(sceneEnd, removedPartEndTime);

      // Create adjusted anchors relative to removed part
      const adjustedStartAnchor = this.createRelativeAnchor(
        scene.anchorStart,
        overlapStart,
        removedPartStartTime,
        removedPartEndTime,
        element.id,
      );

      const adjustedEndAnchor = this.createRelativeAnchor(
        scene.anchorEnd,
        overlapEnd,
        removedPartStartTime,
        removedPartEndTime,
        element.id,
      );

      // Clone scene with adjusted anchors
      const adjustedScene = new Scene({
        id: scene.id,
        anchorStart: adjustedStartAnchor,
        anchorEnd: adjustedEndAnchor,
        layout: scene.layout.clone(),
      });

      snapshots.push(adjustedScene);
    }

    return snapshots;
  }

  /**
   * Creates an anchor relative to the removed part's time range.
   * Converts timeline time to media time relative to the removed segment.
   */
  private createRelativeAnchor(
    originalAnchor: AnchorType,
    timelineTime: number,
    removedPartStartTime: number,
    removedPartEndTime: number,
    elementId: string,
  ): AnchorType {
    // If anchor is clip-start or clip-end, preserve it
    if (typeof originalAnchor === 'string') {
      return originalAnchor;
    }

    // Calculate relative time within removed part (0 = start of removed part)
    const relativeTimeInRemovedPart = timelineTime - removedPartStartTime;

    const end = originalAnchor.end && originalAnchor.elementId === elementId;

    // Create element anchor pointing to the removed element part
    const anchor: ElementAnchor = {
      elementId: elementId,
      relativeTime: end ? null : relativeTimeInRemovedPart,
      end,
    };

    return anchor;
  }

  /**
   * Finds all elements on a track that overlap with a scene's time range
   * @param elements Array of all elements
   * @param trackId Track ID to search
   * @param sceneStartTime Start time of the scene
   * @param sceneEndTime End time of the scene
   * @returns Array of overlapping elements, sorted by time
   */
  findElementsInScene(
    timeline: Timeline,
    scene: IScene,
    trackId: string,
    elements?: Element[],
  ): Element[] {
    // Use provided elements if available, otherwise fall back to timeline.allElements
    const sourceElements = elements ?? timeline.allElements;

    // Find all elements on the specified track
    const candidateElements = trackId
      ? sourceElements.filter(
          el => el.track !== null && el.track.toString() === trackId,
        )
      : sourceElements;

    const sceneStartTime = this.anchorResolver.resolve(
      scene.anchorStart,
      timeline,
    );
    const sceneEndTime = this.anchorResolver.resolve(scene.anchorEnd, timeline);

    // Find all elements that overlap with the scene time range
    const overlappingElements = candidateElements.filter(el => {
      const elDuration = typeof el.duration === 'number' ? el.duration : 0;
      const elStart = el.time;
      const elEnd = el.time + elDuration;

      // Check if element overlaps with scene [sceneStart, sceneEnd]
      return elStart < sceneEndTime && elEnd > sceneStartTime;
    });

    // Sort by time
    return overlappingElements.sort((a, b) => a.time - b.time);
  }

  // =========================================================================
  // Element Media Time Helpers
  // =========================================================================

  /**
   * Gets the media start time for an element.
   * For VideoTranscriptElement, this returns the actual media time (trim_start).
   * For other elements, falls back to timeline time.
   */
  getElementMediaStartTime(element: Element): number {
    if (
      'getMediaStartTime' in element &&
      typeof element.getMediaStartTime === 'function'
    ) {
      return (
        element as { getMediaStartTime: () => number }
      ).getMediaStartTime();
    }
    return element.time;
  }

  /**
   * Gets the media end time for an element.
   * For VideoTranscriptElement, this returns the actual media end time.
   * For other elements, falls back to timeline end_time.
   */
  getElementMediaEndTime(element: Element): number {
    if (
      'getMediaEndTime' in element &&
      typeof element.getMediaEndTime === 'function'
    ) {
      return (element as { getMediaEndTime: () => number }).getMediaEndTime();
    }
    return element.end_time;
  }

  /**
   * Creates an anchor for a given media time point on an element.
   * Uses end: true if target time matches element end (within precision),
   * otherwise sets relative time from start.
   *
   * @param element The element to anchor to
   * @param targetTime The target media time for the anchor
   * @param mediaStartTime The element's media start time
   * @param mediaEndTime The element's media end time
   * @returns ElementAnchor or null if target time is outside element bounds
   */
  createAnchorForMediaTime(
    element: Element,
    targetTime: number,
    mediaStartTime: number,
    mediaEndTime: number,
  ): ElementAnchor | null {
    const PRECISION = 0.001;

    // Check if target time matches element start
    if (Math.abs(targetTime - mediaStartTime) < PRECISION) {
      return {
        elementId: element.id,
        relativeTime: null,
        end: false,
      };
    }

    // Check if target time matches element end - use end: true
    if (Math.abs(targetTime - mediaEndTime) < PRECISION) {
      return {
        elementId: element.id,
        relativeTime: null,
        end: true,
      };
    }

    // Target time is within element - use relativeTime from start
    if (targetTime > mediaStartTime && targetTime < mediaEndTime) {
      const relativeTime = targetTime - mediaStartTime;
      return {
        elementId: element.id,
        relativeTime,
        end: false,
      };
    }

    // Target time is outside element bounds
    logger.warn(
      `Target time ${targetTime} is outside element bounds [${mediaStartTime}, ${mediaEndTime}]`,
    );
    return null;
  }

  /**
   * Finds the scene that contains a specific timeline time.
   *
   * @param timeline The timeline to search
   * @param trackId The track ID to filter scenes by
   * @param timelineTime The timeline time to check
   * @returns The scene containing this time, or null if none found
   */
  findSceneAtTimelineTime(
    timeline: Timeline,
    trackId: string,
    timelineTime: number,
  ): Scene | null {
    const scenesForTrack = this.findScenesByTrack(timeline, trackId);

    for (const scene of scenesForTrack) {
      try {
        const sceneStart = this.anchorResolver.resolve(
          scene.anchorStart,
          timeline,
        );
        const sceneEnd = this.anchorResolver.resolve(scene.anchorEnd, timeline);

        // Check if timelineTime falls within this scene (inclusive start, exclusive end)
        if (timelineTime >= sceneStart && timelineTime < sceneEnd) {
          return scene;
        }
      } catch {
        // Skip scenes with unresolvable anchors
        continue;
      }
    }

    return null;
  }

  /**
   * Converts a media time to timeline time for a given element.
   * Assumes 1:1 playback speed (no time stretching).
   *
   * @param element The element to use for conversion
   * @param mediaTime The media time to convert
   * @returns The corresponding timeline time
   */
  mediaTimeToTimelineTime(element: Element, mediaTime: number): number {
    const elementMediaStart = this.getElementMediaStartTime(element);
    return element.time + (mediaTime - elementMediaStart);
  }
}
